import { useSyncExternalStore } from 'react'

/**
 * Admin "act as" state — app-level, not URL-driven. Relying on a URL param
 * meant every internal link in the whole workflow UI had to explicitly
 * forward it, which is exactly the kind of thing that's easy to miss and
 * silently drops you back to your own identity mid-click. This survives
 * navigation everywhere and is only ever cleared explicitly (the banner's
 * Exit button) or by the tab closing.
 */

const STORAGE_KEY = 'frontflow-admin-acting-as'

export interface ActingAs {
  organizationId: string
  organizationName: string
}

function readStorage(): ActingAs | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ActingAs) : null
  } catch {
    return null
  }
}

function writeStorage(value: ActingAs | null) {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Private browsing / storage disabled — state still works for this tab,
    // it just won't survive a refresh.
  }
}

let current: ActingAs | null = readStorage()
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

export function getActingAs(): ActingAs | null {
  return current
}

export function setActingAs(organizationId: string, organizationName: string) {
  current = { organizationId, organizationName }
  writeStorage(current)
  emit()
}

export function clearActingAs() {
  current = null
  writeStorage(null)
  emit()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useActingAs(): ActingAs | null {
  return useSyncExternalStore(subscribe, getActingAs)
}
